import { ClockInControlsProps } from '@factorialco/factorial-one-react/dist/experimental'
import {
  AttendanceOpenShift,
  TimesheetPageLastWorkingShiftFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import { stringToDate } from 'modules/core/lib/dates'

import { locationTypeText } from 'modules/attendance/lib/locationType'

type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never

export const getNormalizedData = (
  shifts: (
    | TimesheetPageShiftFragment
    | TimesheetPageLastWorkingShiftFragment
    | AttendanceOpenShift
  )[],
  employeeMissingClockOut: boolean
) => {
  const lastShift = shifts?.at(-1)

  const clockInLocationType = lastShift?.locationType ?? undefined

  let clockInLocation: string | undefined
  if (lastShift && 'workplace' in lastShift && lastShift.workplace) {
    clockInLocation = lastShift.workplace.name
  }
  if (!clockInLocation && clockInLocationType) {
    clockInLocation = locationTypeText(clockInLocationType)
  }

  const clockInProject =
    lastShift &&
    'projectManagementTimeRecords' in lastShift &&
    lastShift?.projectManagementTimeRecords?.at(0)?.projectWorker?.imputableProject?.name

  let controlsData: (ArrayElement<NonNullable<ClockInControlsProps['data']>> & {
    id: string | number
    inProgress: boolean
  })[] = shifts.map((shift) => {
    const shiftDate = stringToDate(shift.date, 'backend').toDate()

    const clockInTime = stringToDate(shift.clockIn, 'backend_datetime_without_timezone').toDate()
    const from = new Date(
      shiftDate.getFullYear(),
      shiftDate.getMonth(),
      shiftDate.getDate(),
      clockInTime.getHours(),
      clockInTime.getMinutes(),
      clockInTime.getSeconds()
    )

    const clockOutTime = shift.clockOut
      ? stringToDate(shift.clockOut, 'backend_datetime_without_timezone').toDate()
      : new Date()

    const to = new Date(
      shift.clockOut ? shiftDate.getFullYear() : clockOutTime.getFullYear(),
      shift.clockOut ? shiftDate.getMonth() : clockOutTime.getMonth(),
      shift.clockOut ? shiftDate.getDate() : clockOutTime.getDate(),
      clockOutTime.getHours(),
      clockOutTime.getMinutes(),
      clockOutTime.getSeconds()
    )

    const variant = shift.workable ? (shift.clockOut ? 'clocked-out' : 'clocked-in') : 'break'

    return {
      id: shift.id,
      from,
      to,
      variant,
      inProgress: !shift.clockOut,
    }
  })

  const uniqueDates = new Set(controlsData.map((control) => control.from.getDate()))

  if (uniqueDates.size > 1 && uniqueDates.has(new Date().getDate())) {
    controlsData = controlsData.filter((control) => control.from.getDate() === new Date().getDate())
  }

  let lastControl = controlsData.at(-1)

  if (lastControl?.variant === 'clocked-out' && lastControl.to.getDate() < new Date().getDate()) {
    controlsData = []
  }

  controlsData = controlsData.reduce((acc, control) => {
    if (control.variant === 'clocked-out') {
      acc = [
        { ...control, variant: 'clocked-in' },
        { ...control, id: uuidv4(), from: control.to },
        ...acc,
      ]
      return acc
    }

    acc = [...acc, control]
    return acc
    // eslint-disable-next-line no-type-assertion/no-type-assertion
  }, [] as ControlShift[])

  controlsData = orderBy(controlsData, 'from', 'asc')

  controlsData = controlsData.reduce((acc, control, i) => {
    const previousControl = controlsData?.[i - 1]

    if (previousControl) {
      const differenceMinutes = moment
        .duration(moment(previousControl.to).diff(control.from))
        .asMinutes()

      if (differenceMinutes < 0) {
        acc = [
          ...acc,
          {
            id: uuidv4(),
            from: previousControl.to,
            to: control.from,
            variant: 'clocked-out',
            inProgress: false,
          },
        ]
      }
    }

    acc = [...acc, control]

    return acc
    // eslint-disable-next-line no-type-assertion/no-type-assertion
  }, [] as ControlShift[])

  if (lastControl && lastControl.variant !== 'clocked-out' && !lastControl.inProgress) {
    const newFrom = lastControl.to
    controlsData = [
      ...controlsData,
      {
        id: uuidv4(),
        from: newFrom,
        to: new Date(),
        variant: 'clocked-out',
        inProgress: true,
      },
    ]
  }

  controlsData = uniqBy(controlsData, 'id')

  lastControl = controlsData.at(-1)

  if (lastControl?.variant === 'clocked-in' && employeeMissingClockOut) {
    controlsData = [
      ...controlsData.filter(
        (control) =>
          control.from.getDate() === new Date().getDate() &&
          control.from.getMonth() === new Date().getMonth()
      ),
      { id: uuidv4(), from: new Date(), to: new Date(), variant: 'clocked-out', inProgress: false },
    ]
  }

  return {
    clockInLocation,
    clockInLocationType,
    clockInProject,
    controlsData,
  }
}

type ControlShift = ReturnType<typeof getNormalizedData>['controlsData'][number]

// Helpers for being optimistic when performing actions
const addOptimisticShiftWithVariant = (data: ControlShift[], variant: ControlShift['variant']) => {
  const newData: ControlShift[] = [
    ...data,
    {
      id: uuidv4(),
      from: new Date(),
      to: new Date(),
      variant,
      inProgress: variant !== 'clocked-out',
    },
  ]
  return newData
}

export const performClockInAction: (data: ControlShift[]) => ControlShift[] = (data) => {
  return addOptimisticShiftWithVariant(data, 'clocked-in')
}

export const performBreakStartAction: (data: ControlShift[]) => ControlShift[] = (data) => {
  return addOptimisticShiftWithVariant(data, 'break')
}
